import React, { useEffect, useState } from 'react'
import {
    Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent,
    DialogTitle, Table, TableBody, TableCell, TableHead, TableRow, Tooltip,
    Typography
} from '@mui/material'
import moment from 'moment'
import api from '../services/api'

const CHIP = {
    success: { label: 'OK', color: 'success' },
    error: { label: 'Erro', color: 'error' },
    running: { label: 'Executando', color: 'info' },
    unknown: { label: 'Sem resultado', color: 'default' },
}

/**
 * Histórico das últimas execuções de um host.
 *
 * A coluna Backup da listagem mostra só o estado atual. Quando um backup falha
 * de forma intermitente, o que responde "desde quando" é a sequência — e ela
 * não cabe numa célula.
 */
export default function ExecucoesHostDialog({ open, onClose, admin = false, hostId, hostName }) {
    const [dados, setDados] = useState([])
    const [carregando, setCarregando] = useState(false)
    const [erro, setErro] = useState(false)

    useEffect(() => {
        if (!open || !hostId) return
        setCarregando(true)
        setErro(false)
        const base = admin ? '/admin/hermes/backup' : '/hermes/backup'
        api.get(`${base}/host/${hostId}/executions?limit=30`)
            .then((res) => setDados(res.data?.executions || []))
            .catch(() => { setDados([]); setErro(true) })
            .finally(() => setCarregando(false))
    }, [open, hostId, admin])

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth
            // O tema global tem background.paper escuro com mode 'light'.
            PaperProps={{ sx: { bgcolor: 'background.default', color: 'text.primary' } }}>
            <DialogTitle>
                Últimas execuções
                <Typography variant="body2" color="text.secondary">{hostName}</Typography>
            </DialogTitle>

            <DialogContent sx={{ p: 0 }}>
                {carregando ? (
                    <Box display="flex" justifyContent="center" py={4}><CircularProgress /></Box>
                ) : erro ? (
                    <Typography variant="body2" color="error" sx={{ p: 3 }}>
                        Não foi possível carregar o histórico.
                    </Typography>
                ) : (
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Quando</TableCell>
                                <TableCell>Resultado</TableCell>
                                <TableCell>Detalhe</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {dados.map((exec) => {
                                const chip = CHIP[exec.type] || CHIP.unknown
                                return (
                                    <TableRow key={exec.id}>
                                        <TableCell sx={{ whiteSpace: 'nowrap', fontSize: '0.8rem' }}>
                                            {moment(exec.created_at).locale('pt-br').format('DD/MM/YYYY HH:mm:ss')}
                                        </TableCell>
                                        <TableCell>
                                            <Chip size="small" variant="outlined"
                                                label={chip.label} color={chip.color} />
                                        </TableCell>
                                        <TableCell sx={{ fontSize: '0.75rem', maxWidth: 280 }}>
                                            <Tooltip title={exec.message || ''}>
                                                <span style={{
                                                    display: 'block', overflow: 'hidden',
                                                    textOverflow: 'ellipsis', whiteSpace: 'nowrap'
                                                }}>
                                                    {exec.message}
                                                </span>
                                            </Tooltip>
                                        </TableCell>
                                    </TableRow>
                                )
                            })}
                            {dados.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={3} align="center"
                                        sx={{ py: 4, color: 'text.secondary' }}>
                                        Nenhuma execução registrada para este host.
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                )}
            </DialogContent>

            <DialogActions>
                <Button onClick={onClose} sx={{ textTransform: 'none' }}>Fechar</Button>
            </DialogActions>
        </Dialog>
    )
}
